import React, { ChangeEvent } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FieldColumn, FieldRow } from "../styles";
import { AgentFormState } from "../../../types/agentTypes";
import Provider from "../../../enum/Provider.enum";
import { hasOpenAccount } from "../../../utils/accountActivation";

type NameSectionProps = {
  formData: AgentFormState;
  isCreate: boolean;
  provider: Provider;
  techOnly: boolean;
  loadingTn: boolean;
  tnError: boolean;
  tnClient?: Record<string, any>;
  isFirstNameAmInvalid: boolean;
  isLastNameAmInvalid: boolean;
  isPatronymicAmInvalid: boolean;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<AgentFormState>>;
};

const NameSection = ({
  formData,
  isCreate,
  provider,
  techOnly,
  loadingTn,
  tnError,
  tnClient,
  isFirstNameAmInvalid,
  isLastNameAmInvalid,
  isPatronymicAmInvalid,
  onChange,
  setFormData,
}: NameSectionProps) => {
  const { t } = useTranslation();

  const isArmenia = provider === Provider.ARMENIA;
  const hasTnId = !!String(formData.user_id_tn ?? "").trim();
  const isWorkingStatusAvailable =
    hasTnId && hasOpenAccount(formData.tn_client_status);

  const getAccountStatusMessage = () => {
    if (!hasTnId) {
      return t("createAgent.name.fillTnClientId");
    }

    if (!hasOpenAccount(formData.tn_client_status)) {
      return t("createAgent.name.accountNotOpened");
    }

    return "";
  };

  const renderTnStatus = () => (
    <Box display="flex" alignItems="center" gap={1} minHeight={24}>
      {loadingTn && <CircularProgress size={16} />}

      {!loadingTn && tnError && (
        <Typography variant="body2" color="error">
          {t("createAgent.name.clientNotFound")}
        </Typography>
      )}

      {!loadingTn && tnClient?.full_name && (
        <Typography variant="body2">
          {t("createAgent.name.client", { name: tnClient.full_name })}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      {isCreate ? (
        <>
          <FieldRow>
            <FieldColumn>
              <TextField
                size="small"
                label={
                  isArmenia
                    ? t("createAgent.name.firstNameRu")
                    : t("createAgent.name.firstName")
                }
                name="first_name"
                value={formData.first_name ?? ""}
                fullWidth
                required
                onChange={onChange}
              />
            </FieldColumn>

            <FieldColumn>
              <TextField
                size="small"
                label={
                  isArmenia
                    ? t("createAgent.name.lastNameRu")
                    : t("createAgent.name.lastName")
                }
                name="last_name"
                value={formData.last_name ?? ""}
                fullWidth
                required
                onChange={onChange}
              />
            </FieldColumn>

            <FieldColumn>
              <TextField
                size="small"
                label={
                  isArmenia
                    ? t("createAgent.name.patronymicRu")
                    : t("createAgent.name.patronymic")
                }
                name="patronymic"
                value={formData.patronymic ?? ""}
                fullWidth
                onChange={onChange}
              />
            </FieldColumn>
          </FieldRow>

          {isArmenia && !techOnly && (
            <FieldRow>
              <FieldColumn>
                <TextField
                  size="small"
                  label={t("createAgent.name.firstNameAm")}
                  name="first_name_am"
                  value={formData.first_name_am ?? ""}
                  fullWidth
                  required
                  onChange={onChange}
                  error={isFirstNameAmInvalid}
                  helperText={
                    isFirstNameAmInvalid
                      ? t("createAgent.name.useArmenianChars")
                      : undefined
                  }
                />
              </FieldColumn>

              <FieldColumn>
                <TextField
                  size="small"
                  label={t("createAgent.name.lastNameAm")}
                  name="last_name_am"
                  value={formData.last_name_am ?? ""}
                  fullWidth
                  required
                  onChange={onChange}
                  error={isLastNameAmInvalid}
                  helperText={
                    isLastNameAmInvalid
                      ? t("createAgent.name.useArmenianChars")
                      : undefined
                  }
                />
              </FieldColumn>

              <FieldColumn>
                <TextField
                  size="small"
                  label={t("createAgent.name.patronymicAm")}
                  name="patronymic_am"
                  value={formData.patronymic_am ?? ""}
                  fullWidth
                  onChange={onChange}
                  error={isPatronymicAmInvalid}
                  helperText={
                    isPatronymicAmInvalid
                      ? t("createAgent.name.useArmenianChars")
                      : undefined
                  }
                />
              </FieldColumn>
            </FieldRow>
          )}
        </>
      ) : (
        <>
          <FieldRow>
            <FieldColumn>
              <TextField
                size="small"
                label={t("createAgent.name.fio")}
                name="fio"
                value={formData.fio ?? ""}
                fullWidth
                disabled
              />
            </FieldColumn>
          </FieldRow>

          {isArmenia && !techOnly && (
            <FieldRow>
              <FieldColumn>
                <TextField
                  size="small"
                  label={t("createAgent.name.fioAm")}
                  name="fio_am"
                  value={formData.fio_am ?? ""}
                  fullWidth
                  disabled
                />
              </FieldColumn>
            </FieldRow>
          )}
        </>
      )}

      {isCreate ? (
        <FieldRow style={{ alignItems: "flex-start" }}>
          <FieldColumn>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_have_tn ?? false}
                  onChange={onChange}
                  name="is_have_tn"
                />
              }
              label={t("createAgent.name.hasTnClientId")}
              disabled={!isCreate}
            />

            <FormHelperText>
              {t("createAgent.name.tnClientIdHint")}
            </FormHelperText>
          </FieldColumn>

          {formData.is_have_tn && (
            <FieldColumn>
              <TextField
                size="small"
                label={t("createAgent.name.tnClientId")}
                name="user_id_tn"
                value={formData.user_id_tn ?? ""}
                fullWidth
                required
                onChange={onChange}
              />

              {renderTnStatus()}
            </FieldColumn>
          )}
        </FieldRow>
      ) : (
        <FieldRow style={{ alignItems: "flex-start" }}>
          <FieldColumn>
            <TextField
              size="small"
              label={t("createAgent.name.tnClientId")}
              name="user_id_tn"
              value={formData.user_id_tn ?? ""}
              fullWidth
              disabled
            />

            {renderTnStatus()}
          </FieldColumn>

          <FieldColumn>
            <FormControl fullWidth size="small">
              <InputLabel id="privileges-label">
                {t("createAgent.name.accountType")}
              </InputLabel>

              <Select
                name="privileges"
                label={t("createAgent.name.accountType")}
                labelId="privileges-label"
                value={formData.privileges ?? ""}
                onChange={(e) => {
                  if (e.target.value === "AGENT" && !isWorkingStatusAvailable) {
                    return;
                  }

                  setFormData((prev) => ({
                    ...prev,
                    privileges: e.target.value,
                  }));
                }}
                required
                fullWidth
                disabled={!hasTnId}
              >
                <MenuItem value="NEW">
                  {t("createAgent.name.accountTypeNew")}
                </MenuItem>

                <MenuItem value="AGENT" disabled={!isWorkingStatusAvailable}>
                  {t("createAgent.name.accountTypeAgent")}
                </MenuItem>
              </Select>

              {!isWorkingStatusAvailable && (
                <FormHelperText error={hasTnId}>
                  {getAccountStatusMessage()}
                </FormHelperText>
              )}
            </FormControl>
          </FieldColumn>
        </FieldRow>
      )}
    </>
  );
};

export default NameSection;
